


















import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import {
  PerspectiveCamera,
  Scene,
  Mesh,
  WebGLRenderer,
  Vector2,
  Raycaster,
  sRGBEncoding,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

import {
  addAmbientLightToScene,
  addPointLightToScene,
  addPointMeshToScene,
  createOrbitControls,
  createPerspectiveCamera,
  createRenderer,
  createScene,
  getContainer,
} from '@/helpers/three';
import { getColor } from '@/helpers/colors';
import { Track } from '@/config';

import Player from './components/player/player.vue';
import ProgressDetails from './components/loading-bar/progress-details.vue';

interface IData {
  camera: PerspectiveCamera | null;
  scene: Scene | null;
  renderer: WebGLRenderer | null;
  controls: OrbitControls | null;
  mouse: Vector2;
  points: Mesh[];
  raycaster: Raycaster | null;
  hovered: number;
  playing: number;
  lastFrame: number;
  framesPerSeconds: number;
}

interface IComputed {
  getTracks: Array<Track>;
  getGraph: Array<Array<number>>;
  getFirstAndLast: Array<number>;
  isProcessDone: boolean;
  isAuthenticated: boolean;
  timeToReset: number;
  getUpdate: number;
}

interface IMethods {
  processData: () => void;
  handleClick: () => void;
  addPoints: () => void;
  trackMouse: (e: MouseEvent) => void;
  initialize: () => void;
  animate: () => void;
  resize: () => void;
}

export default Vue.extend<IData, IMethods, IComputed>({
  name: 'Cartography',

  components: {
    Player,
    ProgressDetails,
  },

  data: () => ({
    camera: null as PerspectiveCamera | null,
    scene: null as Scene | null,
    renderer: null as WebGLRenderer | null,
    controls: null as OrbitControls | null,
    mouse: new Vector2(0, 0),
    points: [] as Mesh[],
    raycaster: null as Raycaster | null,
    hovered: -1 as number,
    playing: -1 as number,
    lastFrame: Date.now() as number,
    framesPerSeconds: 0 as number,
  }),

  computed: {
    ...mapGetters('data', [
      'getTracks',
      'getFirstAndLast',
    ]),

    ...mapGetters('map', [
      'getGraph',
      'isProcessDone',
      'getUpdate',
    ]),

    ...mapGetters('auth', [
      'isAuthenticated',
    ]),

    timeToReset() {
      return this.getUpdate;
    },
  },

  async mounted() {
    if (!this.isAuthenticated && Object.keys(this.getTracks).length === 0) {
      this.$router.push('/');
    }
    if (Object.keys(this.getTracks).length === 0) {
      this.$router.push('/exploring');
    }

    if (this.isAuthenticated) {
      await this.$store.dispatch('map/firstProcess');
    }

    await this.initialize();
    this.animate();

    window.addEventListener('resize', this.resize);
    window.addEventListener('mousemove', this.trackMouse);
  },

  watch: {
    timeToReset() {
      if (this.isProcessDone) {
        this.addPoints();
      }
    },
  },

  methods: {
    ...mapActions('map', [
      'processData',
    ]),

    handleClick() {
      if (this.hovered !== -1) {
        if (this.isAuthenticated) {
          this.$store.dispatch('player/play', this.getTracks[this.hovered].id);
          this.playing = this.hovered;
        }
      }
    },

    addPoints() {
      if (!this.getGraph.length || this.renderer === null) {
        return;
      }

      // Remove all old points.
      for (let i = 0; i < this.points.length; i += 1) {
        (this.scene as Scene).remove(this.points[i]);
      }

      this.points = [];

      const firstAndLast = this.getFirstAndLast as number[];
      const first = firstAndLast[0];
      const last = firstAndLast[1];

      let largestValue = 0;

      for (let i = 0; i < this.getGraph.length; i += 1) {
        for (let j = 0; j < this.getGraph[i].length; j += 1) {
          if (this.getGraph[i][j] > largestValue) {
            largestValue = this.getGraph[i][j];
          }
        }
      }

      const multiplier = 1;

      // Add new points.
      for (let i = 0; i < this.getGraph.length; i += 1) {
        if (this.getTracks[i] !== undefined) {
          const color = getColor(this.getTracks[i].attatchedData.calculatedValue);

          this.points.push(addPointMeshToScene(
            this.scene as Scene,
            (this.getGraph[i][0] / largestValue) * multiplier,
            (this.getGraph[i][1] / largestValue) * multiplier,
            (this.getGraph[i][2] / largestValue) * multiplier,
            color,
          ));
        }
      }
    },

    trackMouse(e: MouseEvent) {
      this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      this.mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
    },

    async initialize() {
      const container = (getContainer() as HTMLElement);
      this.raycaster = new Raycaster();
      this.camera = createPerspectiveCamera(container);
      this.scene = createScene();
      this.controls = createOrbitControls(this.camera, container);

      addPointLightToScene(this.scene);
      addAmbientLightToScene(this.scene);

      this.renderer = createRenderer(container);
      this.renderer.outputEncoding = sRGBEncoding;

      if (this.getGraph.length && !this.points.length) {
        this.addPoints();
      }
    },

    animate() {
      requestAnimationFrame(this.animate);

      const now = Date.now();
      const milliseconds = now - this.lastFrame;
      this.framesPerSeconds = Math.floor(1000 / milliseconds);
      this.lastFrame = now;

      (this.raycaster as Raycaster).setFromCamera(this.mouse, this.camera as PerspectiveCamera);
      const intersections = (this.raycaster as Raycaster).intersectObjects(this.points);

      if (intersections.length) {
        const { uuid } = intersections[0].object;

        for (let i = 0; i < this.points.length; i += 1) {
          if (this.points[i].uuid === uuid) {
            this.hovered = i;
            break;
          }
        }
      } else {
        this.hovered = -1;
      }

      if (this.isProcessDone) {
        for (let i = 0; i < this.points.length; i += 1) {
          if (this.getTracks[i] && 'audioFeatures' in this.getTracks[i]) {
            this.points[i].rotation.y += this.getTracks[i].audioFeatures.energy / 10;
          }
        }
      }

      // eslint-disable-next-line max-len
      (this.renderer as WebGLRenderer).render((this.scene as Scene), (this.camera as PerspectiveCamera));
    },

    resize() {
      const container = document.getElementById('container') as HTMLElement;

      (this.renderer as WebGLRenderer).setSize(container.clientWidth, container.clientHeight);
    },
  },
});
